<template>
  <div ref="scroller" class="settings-scroll" :style="{ maxHeight: scrollHeight }">
    <div class="settings">
      <p class="help settings-intro">
        Valores generales de la app móvil. Si un campo queda vacío, la app usa el valor indicado en gris.
        Los cambios llegan a la app sin publicar una nueva versión (al abrirla o en máximo 5 minutos).
      </p>

      <h3 class="title is-5">Redes sociales</h3>
      <div class="field" v-for="field in socialFields" :key="field.key">
        <label class="label" :for="field.key">{{ field.label }}</label>
        <input
          :id="field.key"
          v-model.trim="form[field.key]"
          class="input"
          :class="{ 'is-danger': errors[field.key] }"
          type="text"
          inputmode="url"
          :placeholder="field.placeholder"
        />
        <p v-if="errors[field.key]" class="help is-danger">{{ errors[field.key] }}</p>
      </div>

      <h3 class="title is-5">Compartir la app</h3>
      <div class="field">
        <label class="label" for="shareUrl">Link para compartir</label>
        <input
          id="shareUrl"
          v-model.trim="form.shareUrl"
          class="input"
          :class="{ 'is-danger': errors.shareUrl }"
          type="text"
          inputmode="url"
          :placeholder="defaults.shareUrl"
        />
        <p v-if="errors.shareUrl" class="help is-danger">{{ errors.shareUrl }}</p>
      </div>
      <div class="field">
        <label class="label" for="shareMessage">Mensaje para compartir</label>
        <input
          id="shareMessage"
          v-model.trim="form.shareMessage"
          class="input"
          type="text"
          maxlength="140"
          :placeholder="defaults.shareMessage"
        />
        <p class="help">Al compartir se envía este mensaje seguido del link.</p>
      </div>

      <h3 class="title is-5">Colores</h3>
      <div class="field">
        <label class="label" for="accentColor">Color de resaltado del nombre de la emisora</label>
        <div class="color-row">
          <input
            type="color"
            :value="previewColor"
            @input="form.accentColor = $event.target.value"
            aria-label="Selector de color"
          />
          <input
            id="accentColor"
            v-model.trim="form.accentColor"
            class="input color-text"
            :class="{ 'is-danger': errors.accentColor }"
            type="text"
            :placeholder="defaults.accentColor"
          />
          <span class="preview">
            MAD <span class="preview-highlight" :style="{ backgroundColor: previewColor }">RECORDS</span>
          </span>
        </div>
        <p v-if="errors.accentColor" class="help is-danger">{{ errors.accentColor }}</p>
      </div>

      <div class="field actions">
        <a class="button is-dark" :class="{ 'is-loading': saving }" @click="save">Guardar</a>
        <span v-if="status" class="help" :class="statusError ? 'is-danger' : 'is-success'">{{ status }}</span>
      </div>
    </div>
  </div>
</template>

<script>
import { db } from '../services/firebase-init'

const SETTINGS_PATH = '/form/settings'
const URL_PATTERN = /^https?:\/\/\S+$/i
const COLOR_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i

// Must match DEFAULT_SETTINGS in the mobile app (src/config/env.ts).
const DEFAULTS = {
  facebookUrl: 'https://www.facebook.com/madradio.co/',
  instagramUrl: 'https://www.instagram.com/madradio.co/',
  youtubeUrl: 'https://www.youtube.com/channel/UCrwb4mwq8i0kruOasp2Yrtg',
  webUrl: 'https://madradio.co/',
  shareUrl: 'https://madradio.co/app',
  shareMessage: '¡Escucha Mad Radio, música 24/7!',
  accentColor: '#d05753'
}

const FIELDS = Object.keys(DEFAULTS)
const URL_FIELDS = ['facebookUrl', 'instagramUrl', 'youtubeUrl', 'webUrl', 'shareUrl']

function emptyForm () {
  return FIELDS.reduce((form, key) => ({ ...form, [key]: '' }), {})
}

export default {
  name: 'settings',
  data () {
    return {
      defaults: DEFAULTS,
      form: emptyForm(),
      errors: {},
      saving: false,
      scrollHeight: 'none',
      status: '',
      statusError: false,
      socialFields: [
        { key: 'facebookUrl', label: 'Facebook', placeholder: DEFAULTS.facebookUrl },
        { key: 'instagramUrl', label: 'Instagram', placeholder: DEFAULTS.instagramUrl },
        { key: 'youtubeUrl', label: 'YouTube', placeholder: DEFAULTS.youtubeUrl },
        { key: 'webUrl', label: 'Sitio web', placeholder: DEFAULTS.webUrl }
      ]
    }
  },
  computed: {
    previewColor () {
      return COLOR_PATTERN.test(this.form.accentColor) ? this.form.accentColor : DEFAULTS.accentColor
    }
  },
  async created () {
    const snapshot = await db.ref(SETTINGS_PATH).once('value')
    const saved = snapshot.val() || {}
    this.form = FIELDS.reduce((form, key) => ({ ...form, [key]: saved[key] || '' }), {})
  },
  mounted () {
    this.fitToWindow()
    window.addEventListener('resize', this.fitToWindow)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.fitToWindow)
  },
  methods: {
    // The panel disables page scroll (<html style="overflow: hidden">, also inside the WordPress iframe),
    // so this tab scrolls on its own within the visible height.
    fitToWindow () {
      const top = this.$refs.scroller.getBoundingClientRect().top
      this.scrollHeight = Math.max(window.innerHeight - top - 16, 240) + 'px'
    },
    validate () {
      const errors = {}
      URL_FIELDS.forEach((key) => {
        if (this.form[key] && !URL_PATTERN.test(this.form[key])) {
          errors[key] = 'Debe ser un link completo que empiece por https://'
        }
      })
      if (this.form.accentColor && !COLOR_PATTERN.test(this.form.accentColor)) {
        errors.accentColor = 'Usa un color en formato #RRGGBB, por ejemplo #d05753'
      }
      this.errors = errors
      return Object.keys(errors).length === 0
    },
    async save () {
      this.status = ''
      if (!this.validate()) return
      this.saving = true
      // Empty fields are stored as null so Firebase removes them and the app falls back to its defaults.
      const payload = FIELDS.reduce((data, key) => ({ ...data, [key]: this.form[key] || null }), {})
      try {
        await db.ref(SETTINGS_PATH).update(payload)
        this.statusError = false
        this.status = 'Guardado'
      } catch (e) {
        this.statusError = true
        this.status = 'No fue posible guardar: ' + e.message
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style scoped>
.settings-scroll {
  overflow-y: auto;
  padding-bottom: 24px;
}
.settings {
  max-width: 560px;
  margin: 0 auto;
  text-align: left;
}
.settings-intro {
  margin-bottom: 24px;
  font-size: 14px;
}
.settings .title {
  margin-top: 28px;
}
.color-row {
  display: flex;
  align-items: center;
  gap: 12px;
}
.color-row input[type='color'] {
  width: 48px;
  height: 36px;
  padding: 0;
  border: none;
  background: none;
  cursor: pointer;
}
.color-text {
  max-width: 140px;
}
.preview {
  font-weight: bold;
  letter-spacing: 1px;
}
.preview-highlight {
  color: #ffffff;
  padding: 2px 8px;
}
.actions {
  margin-top: 32px;
  display: flex;
  align-items: center;
  gap: 16px;
}
</style>
